// apps/provider/src/services/api/oasis.js
// Home Health: OASIS-E2 specification and assessment records. Every endpoint
// is solution-gated server-side (hh_require_access, permission hh_oasis).
//
// Xano tables: oasis_item_spec, oasis_edit_spec, oasis_isc_spec, oasis_assessment
// Response types: @prism-ehr/shared → OasisSpecResponse, OasisAssessment,
// OasisAssessmentListResponse, OasisSeedResponse
//
// Dates in and out of these endpoints are 'YYYY-MM-DD' strings; responses are
// keyed by item id with the string value exactly as it goes into the XML.

/**
 * Creates OASIS methods. Mixed into ApiService via Object.assign().
 */
export function createOasisMethods() {
  return {

    /**
     * The seeded specification (items with values, edits, ISC mapping) for one
     * data-spec version; default = the version in effect for as_of (today).
     * Xano: GET /oasis/spec
     * @param {Object} [params]
     * @param {string} [params.spec_version]
     * @param {string} [params.as_of] - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').OasisSpecResponse>}
     */
    async getOasisSpec(params = {}) {
      const queryParams = new URLSearchParams();
      if (params.spec_version) queryParams.append('spec_version', params.spec_version);
      if (params.as_of) queryParams.append('as_of', params.as_of);
      const qs = queryParams.toString();
      return this.request(`/oasis/spec${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Admin: upsert one spec version from the generated JSON (scripts/oasis-spec-seed.mjs
     * is the usual caller). Idempotent.
     * Xano: POST /oasis/spec/seed
     * @param {import('@prism-ehr/shared').OasisSeedRequest} body
     * @returns {Promise<import('@prism-ehr/shared').OasisSeedResponse>}
     */
    async seedOasisSpec(body) {
      return this.request('/oasis/spec/seed', {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'seedOasisSpec');
    },

    /**
     * Paged list of the organization's assessments, newest first.
     * Xano: GET /oasis/assessments
     * @param {import('@prism-ehr/shared').OasisAssessmentListParams} [filters]
     * @returns {Promise<import('@prism-ehr/shared').OasisAssessmentListResponse>}
     */
    async listOasisAssessments(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.case_id) queryParams.append('case_id', String(filters.case_id));
      if (filters.hh_episode_id) queryParams.append('hh_episode_id', String(filters.hh_episode_id));
      if (filters.assessment_reason) queryParams.append('assessment_reason', filters.assessment_reason);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);
      if (filters.page) queryParams.append('page', String(filters.page));
      if (filters.per_page) queryParams.append('per_page', String(filters.per_page));
      const qs = queryParams.toString();
      return this.request(`/oasis/assessments${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Create a prefilled draft on an episode. Refused when an open draft of the
     * same reason exists or the episode's status does not allow the reason.
     * Xano: POST /oasis/assessments
     * @param {import('@prism-ehr/shared').OasisCreateAssessmentRequest} body
     * @returns {Promise<import('@prism-ehr/shared').OasisAssessment>}
     */
    async createOasisAssessment(body) {
      return this.request('/oasis/assessments', {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'createOasisAssessment');
    },

    /**
     * One assessment with its responses.
     * Xano: GET /oasis/assessments/{assessment_id}
     * @param {number} assessmentId
     * @returns {Promise<import('@prism-ehr/shared').OasisAssessment>}
     */
    async getOasisAssessment(assessmentId) {
      return this.request(`/oasis/assessments/${assessmentId}`, { method: 'GET' });
    },

    /**
     * Merge a partial responses object (and dates / links) into a draft. A key
     * set to '' removes the answer. offline_updated_at older than the row's
     * updated_at is refused (message starts with stale_offline_update).
     * Xano: PATCH /oasis/assessments/{assessment_id}
     * @param {number} assessmentId
     * @param {import('@prism-ehr/shared').OasisUpdateAssessmentRequest} body
     * @returns {Promise<import('@prism-ehr/shared').OasisAssessment>}
     */
    async updateOasisAssessment(assessmentId, body) {
      return this.request(`/oasis/assessments/${assessmentId}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      }, 'updateOasisAssessment');
    },
  };
}
